import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-shipping';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Доставка из Китая с YOYbuy Sheet: способы, сроки, стоимость и как сэкономить на пересылке',
  description: 'Всё о международной доставке из Китая через yoybuy spreadsheet. Авиаперевозка, морской фрахт, экспресс-службы — сравниваем сроки, цены и выбираем оптимальный способ.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20photography%20of%20a%20neatly%20stacked%20cardboard%20shipping%20boxes%20in%20various%20sizes%20arranged%20on%20a%20clean%20concrete%20floor%20near%20a%20large%20industrial%20window%2C%20soft%20natural%20daylight%2C%20warm%20cream%20and%20beige%20tones%2C%20logistics%20and%20shipping%20concept%2C%20editorial%20warehouse%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20box%20textures%20and%20labels&width=1400&height=700&seq=article-shipping-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function ShippingArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Доставка</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Доставка из Китая с YOYbuy Sheet: способы, сроки, стоимость и как сэкономить на пересылке
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Всё о международной доставке из Китая через <strong>yoybuy spreadsheet</strong>. Авиаперевозка, морской фрахт, экспресс-службы — сравниваем сроки, цены и выбираем оптимальный способ.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 8 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20photography%20of%20a%20neatly%20stacked%20cardboard%20shipping%20boxes%20in%20various%20sizes%20arranged%20on%20a%20clean%20concrete%20floor%20near%20a%20large%20industrial%20window%2C%20soft%20natural%20daylight%2C%20warm%20cream%20and%20beige%20tones%2C%20logistics%20and%20shipping%20concept%2C%20editorial%20warehouse%20photography%20style%2C%20clean%20aesthetic%2C%20high%20detail%20on%20box%20textures%20and%20labels&width=1400&height=700&seq=article-shipping-hero-2026&orientation=landscape"
                  alt="Доставка из Китая через YOYbuy Sheet"
                  title="YOYbuy Sheet доставка — способы и стоимость"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как работает доставка в YOYbuy Spreadsheet
                  </h2>
                  <p>
                    Доставка — ключевой этап при заказе товаров из Китая. От выбора способа пересылки зависит скорость получения, итоговая стоимость и сохранность товара. <strong>Yoybuy spreadsheet</strong> делает этот этап прозрачным: в таблице есть отдельные колонки для веса каждого товара, способа доставки, страховки и расчётной стоимости. Вы видите полную картину до отправки заказа агенту.
                  </p>
                  <p>
                    Встроенный калькулятор <strong>yoybuy sheet</strong> автоматически суммирует вес всех позиций и рассчитывает примерную стоимость доставки для выбранного способа. Вы можете сравнить варианты: например, авиадоставка за 7–14 дней обойдётся в X долларов, а морская за 25–40 дней — в Y долларов. Решение принимаете вы, имея на руках полные данные.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Способы доставки из Китая: полный обзор
                  </h2>
                  <div className="space-y-4">
                    {[
                      { name: 'Авиадоставка (Air Mail)', timing: '7–21 день', desc: 'Самый популярный способ для посылок до 2 кг. Стоимость: $8–20 за первые 500 г. В yoybuy sheet укажите точный вес каждого товара — таблица рассчитает итог. Отлично подходит для аксессуаров, очков, бижутерии.' },
                      { name: 'Экспресс-доставка (DHL, FedEx, EMS)', timing: '3–10 дней', desc: 'Быстро, но дороже. Стоимость: $25–60 за посылку. В yoybuy spreadsheet отметьте, нужна ли вам курьерская доставка до двери или получение в пункте выдачи. Для срочных заказов и дорогих товаров.' },
                      { name: 'Морская доставка (Sea Freight)', timing: '25–45 дней', desc: 'Самый экономичный способ для больших и тяжёлых посылок (от 10 кг). Стоимость: $3–8 за кг. В таблице YOYbuy укажите габариты коробки — это влияет на итоговую цену морского фрахта.' },
                      { name: 'Железнодорожная доставка', timing: '15–25 дней', desc: 'Золотая середина: быстрее моря, дешевле авиа. Особенно популярна для доставки в Европу и Россию. В yoybuy sheet отметьте этот способ как компромиссный вариант.' },
                    ].map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground-900">{item.name}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">{item.timing}</span>
                          </div>
                          <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20an%20open%20cardboard%20box%20on%20a%20light%20wooden%20floor%20with%20packing%20materials%20and%20bubble%20wrap%20visible%2C%20soft%20natural%20light%20from%20a%20nearby%20window%2C%20warm%20beige%20and%20cream%20color%20palette%2C%20shipping%20and%20packaging%20concept%2C%20editorial%20product%20photography%20style%2C%20clean%20composition%2C%20high%20detail%20on%20cardboard%20texture&width=1200&height=600&seq=article-shipping-boxes&orientation=landscape"
                    alt="YOYbuy Sheet — доставка из Китая, упаковка посылки"
                    title="Способы доставки через YOYbuy Spreadsheet из Китая"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как рассчитать стоимость доставки в YOYbuy Sheet
                  </h2>
                  <p>
                    Формула проста: вес посылки × тариф за кг + фиксированный сбор. В <strong>yoybuy spreadsheet</strong> вы указываете вес каждого товара в отдельной строке, а таблица суммирует все значения. Допустим, вы заказываете пару кроссовок (800 г), худи (500 г) и рюкзак (700 г) — общий вес 2 кг. При авиадоставке по $12/кг итог составит около $24. Плюс фиксированный сбор $5. Итого примерно $29 за доставку трёх вещей. Все расчёты — прямо в таблице, без отдельных калькуляторов.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Страховка посылки: нужна ли она?
                  </h2>
                  <p>
                    В <strong>yoybuy sheet</strong> есть колонка «Страховка» с вариантами: базовая (бесплатно, компенсация до $50) или расширенная (1–3% от стоимости товара, полная компенсация). Для дорогих заказов — курток, часов, кожаных сумок — рекомендуем всегда выбирать расширенную страховку. Для недорогих аксессуаров и одежды достаточно базовой. Таблица YOYbuy автоматически добавит стоимость страховки в итоговый расчёт.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Доставка из Китая не должна быть «котом в мешке». С <strong>yoybuy spreadsheet</strong> вы точно знаете, сколько весит каждый товар, сколько стоит его доставка и когда он прибудет. Сравнивайте способы, выбирайте оптимальный и экономьте. <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Вернитесь на главную</Link> и начните заказ. Читайте также: <Link to="/yoybuy-spreadsheet-safety" className="text-primary-600 hover:text-primary-700 underline font-medium">безопасность покупок</Link>, <Link to="/yoybuy-spreadsheet-taobao" className="text-primary-600 hover:text-primary-700 underline font-medium">заказ с Taobao</Link> и <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-safety" className="text-xs text-primary-600 hover:text-primary-700 underline">Безопасность покупок</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-vs-agents" className="text-xs text-primary-600 hover:text-primary-700 underline">Таблицы vs Агенты</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}